import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Paper from 'components/system/Paper';
import { createGradientColoredColumn } from 'utils/chart';
import { gradientDarkColor, gradientLightColor } from 'utils/constants';
import colors from 'styles/colors';
import FullScreenResizer from 'components/system/FullScreenResizer';
import StatevectorLoader from './ContentLoader/StatevectorLoader';
import JobDetailExportButton from './JobDetailExportButton';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

export const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  maxBarThickness: 10,
  borderRadius: 4,
};

const JobStateVectorChart = ({
  isLoading,
  chartData,
  canExport,
  statevectorResourceLink,
  numQubits,
}) => {
  const labels = [];
  const values = [];

  chartData && Object.entries(chartData).sort((a, b) => {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
  }).forEach(([key, value]) => {
    values.push(value);
    labels.push(key);
  });
  const maxValue = Math.max(...values);

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: createGradientColoredColumn(
          maxValue,
          gradientDarkColor,
          gradientLightColor,
        ),
      },
    ],
  };

  let innerContent;

  if (isLoading) {
    innerContent = <StatevectorLoader />;
  } else if (data.labels.length !== 0) {
    innerContent = (
      <FullScreenResizer title="Statevector" contentStyles={{ height: '230px' }}>
        <Bar options={options} data={data} />
      </FullScreenResizer>
    );
  } else {
    innerContent = (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color={colors.text.primary}>
          No Data
        </Typography>
      </Box>
    );
  }

  let tooltipTitle;

  if (canExport) {
    tooltipTitle = (
      <>
        Download the final statevector of the simulated circuit.
        {' '}
        These are 2
        <sup>n</sup>
        {' '}
        complex numbers describing the final quantum state.
      </>
    );
  } else if (!canExport && numQubits > 16) {
    tooltipTitle = 'Statevector is not available for circuits with > 16 qubits';
  } else {
    tooltipTitle = 'Statevector is not available for jobs with Observable output';
  }

  return (
    <Paper>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <JobDetailExportButton
          onClick={() => window.open(`${statevectorResourceLink}statevector.txt`, '_blank')}
          buttonText="Statevector"
          disabled={!canExport}
          sx={{ marginBottom: 0 }}
        />
        <Tooltip
          arrow
          sx={{ backgroundColor: colors.background.primary }}
          title={tooltipTitle}
          placement="right"
        >
          <InfoOutlinedIcon
            sx={{
              color: colors.text.secondary,
              fontSize: '16px',
              paddingLeft: '5px',
              paddingBottom: '5px',
            }}
          />
        </Tooltip>
      </Box>
      <Paper sx={{ height: '250px' }}>
        {innerContent}
      </Paper>
    </Paper>

  );
};

export default JobStateVectorChart;
